<script setup>
import { ref, onMounted, onUnmounted, watch } from 'vue'

const props = defineProps({
  visible: { type: Boolean, default: false },
  deepBgCanvas: { type: Object, default: null }, // canvas element inside section (z-0, behind text)
})

const canvasRef = ref(null)
const canvasMidRef = ref(null)
const canvasFgRef = ref(null)
const canvasFgCloudsRef = ref(null)
let ctx = null
let ctxMid = null
let ctxFg = null
let ctxFgClouds = null
let ctxDeepBg = null
let animationFrameId = null
let width = 0
let height = 0
const isDay = ref(false)
let observer = null

let isAnimating = false

const mouse = {
  x: 0,
  y: 0,
}

// ─── Perch / landing state ──────────────────────────────────────────────────
let lastMouseMoveTime = performance.now()
let prevMouseTime = performance.now()
let perchedInsects = []
const STILL_DURATION = 1800 // ms before a butterfly lands on cursor
const TAKEOFF_VELOCITY = 0.18 // px/ms — only fast swipes trigger takeoff

// --- Arrays for all elements ---
let insects = [] // Butterflies / Fireflies
let stars = [] // Night BG
let shootingStars = [] // Night BG
let clouds = [] // Day BG (behind text)
let fgClouds = [] // Day FG (above text)
let physicsLeaves = [] // Day — leaves falling from trees

// --- Config ---
const getInsectCount = () => (isDay.value ? 18 : 11)
const getFgCount = () => (isDay.value ? 6 : 4)
const DEEP_BG_COUNT = 3 // insects rendered behind the text layer

// Night BG
const STAR_COUNT = 150
let nextMeteorFrame = Math.random() * 600 + 200

// Day BG
const CLOUD_COUNT = 14
const CANOPY_COUNT = 18
let canopies = []

// Tree canopy shape templates (puffs: {rx, ry, ox, oy})
const CANOPY_TEMPLATES = [
  // Round bushy
  [
    { rx: 14, ry: 10, ox: 0, oy: 0 },
    { rx: 11, ry: 7, ox: -8, oy: -4 },
    { rx: 10, ry: 6, ox: 7, oy: 2 },
    { rx: 8, ry: 5, ox: -2, oy: 5 },
  ],
  // Tall pine-like
  [
    { rx: 8, ry: 13, ox: 0, oy: 0 },
    { rx: 7, ry: 8, ox: 0, oy: -8 },
    { rx: 6, ry: 7, ox: 0, oy: 7 },
  ],
  // Wide canopy
  [
    { rx: 13, ry: 7, ox: 0, oy: -6 },
    { rx: 16, ry: 7, ox: 0, oy: 2 },
    { rx: 9, ry: 6, ox: -5, oy: -2 },
    { rx: 8, ry: 5, ox: 6, oy: 0 },
  ],
  // Compact cluster
  [
    { rx: 9, ry: 8, ox: -4, oy: -3 },
    { rx: 8, ry: 7, ox: 6, oy: 1 },
    { rx: 11, ry: 8, ox: 0, oy: 5 },
  ],
  // Layered oak
  [
    { rx: 15, ry: 8, ox: 0, oy: 4 },
    { rx: 13, ry: 6, ox: -3, oy: -2 },
    { rx: 12, ry: 7, ox: 4, oy: -5 },
    { rx: 9, ry: 5, ox: -7, oy: 3 },
    { rx: 8, ry: 5, ox: 7, oy: 2 },
  ],
  // Pine / conifer — stacked triangular tiers
  [
    { rx: 4, ry: 5, ox: 0, oy: -11 },
    { rx: 7, ry: 5, ox: 0, oy: -4 },
    { rx: 10, ry: 5, ox: 0, oy: 3 },
    { rx: 5, ry: 8, ox: 0, oy: -2 },
  ],
  // Sprawling ancient tree — wide and asymmetric
  [
    { rx: 18, ry: 9, ox: 0, oy: 2 },
    { rx: 13, ry: 7, ox: -9, oy: -3 },
    { rx: 12, ry: 8, ox: 10, oy: -4 },
    { rx: 9, ry: 5, ox: -15, oy: 1 },
    { rx: 8, ry: 5, ox: 15, oy: 0 },
    { rx: 7, ry: 5, ox: 2, oy: -9 },
  ],
  // Compact round shrub
  [
    { rx: 11, ry: 10, ox: 0, oy: 0 },
    { rx: 8, ry: 7, ox: -5, oy: -2 },
    { rx: 8, ry: 7, ox: 5, oy: -2 },
    { rx: 7, ry: 6, ox: 0, oy: -6 },
  ],
]

// --- UTILS ---
const drawSprite = (ctx, x, y, size, colorMap, spriteGrid) => {
  const pixelSize = size
  spriteGrid.forEach((row, rowIndex) => {
    row.forEach((col, colIndex) => {
      if (col !== 0 && colorMap[col]) {
        ctx.fillStyle = colorMap[col]
        ctx.fillRect(x + colIndex * pixelSize, y + rowIndex * pixelSize, pixelSize, pixelSize)
      }
    })
  })
}

// Moon phase (0 = new, 0.25 = first quarter, 0.5 = full, 0.75 = last quarter)
function getMoonPhase() {
  const knownNewMoon = Date.UTC(2000, 0, 6, 18, 14, 0)
  const synMonthMs = 29.530588861 * 86400000
  const elapsed = Date.now() - knownNewMoon
  let phase = (elapsed / synMonthMs) % 1
  if (phase < 0) phase += 1
  return phase
}

// Dark-circle offset for moon phase rendering
function moonDarkOffset(phase, r) {
  // Dark circle center offset from moon center
  // Phase 0:     0  (fully covered)
  // Phase 0.25: -r  (half covered from left / right half lit)
  // Phase 0.5:  -2r (off left, full moon)
  // Phase 0.75: +r  (half covered from right / left half lit)
  // Phase 1.0:   0  (fully covered again)
  if (phase <= 0.25) return -(phase / 0.25) * r
  if (phase <= 0.5) return -r - ((phase - 0.25) / 0.25) * r
  if (phase <= 0.75) return 2 * r - ((phase - 0.5) / 0.25) * r
  return r - ((phase - 0.75) / 0.25) * r
}

// Rounded pixel-puff: staggered rows approximating an ellipse
function drawPuff(cx, cy, rx, ry, color) {
  ctx.fillStyle = color
  const rows = ry * 2 + 1
  for (let r = 0; r < rows; r++) {
    const dy = r - ry
    const ratio = Math.sqrt(Math.max(0, 1 - (dy * dy) / (ry * ry || 1)))
    const w = Math.max(1, Math.floor(rx * 2 * ratio))
    const x = cx - Math.floor(w / 2)
    ctx.fillRect(x, cy + dy, w, 1)
  }
}

// --- SPRITES (Insects) ---
const BUTTERFLY_VARIANTS = [
  { 1: '#2d2d2e', 2: '#38a7e7', 3: '#d4eeff' }, // Blue Morpho
  { 1: '#1c0f00', 2: '#f97316', 3: '#fff7ed' }, // Monarch
  { 1: '#1a1a00', 2: '#f59e0b', 3: '#fefce8' }, // Yellow Swallowtail
  { 1: '#1e0535', 2: '#a855f7', 3: '#f0e8ff' }, // Violet
]

const butterflyOpen = [
  [2, 2, 1, 2, 2],
  [2, 3, 1, 3, 2],
  [0, 2, 1, 2, 0],
  [0, 0, 1, 0, 0],
]

const butterflyClosed = [
  [0, 2, 1, 2, 0],
  [0, 2, 1, 2, 0],
  [0, 0, 1, 0, 0],
  [0, 0, 1, 0, 0],
]

const fireflySpriteDesign = [
  [0, 1, 1, 0],
  [2, 1, 1, 2],
  [0, 3, 3, 0],
]

// --- CLASSES ---

/* ===========================
   INSECT (Butterfly / Firefly)
   =========================== */
class Insect {
  constructor() {
    this.init()
  }

  init() {
    this.x = Math.random() * width
    this.y = Math.random() * height
    this.vx = (Math.random() - 0.5) * 1.0
    this.vy = (Math.random() - 0.5) * 0.8
    this.size = isDay.value ? 3 : 4

    this.wingState = 'open'
    this.glowIntensity = 0
    this.isForeground = false

    if (isDay.value) {
      // ─── Wander steering (Craig Reynolds, tuned for butterfly) ─────────
      this.wanderTheta = Math.random() * Math.PI * 2
      this.wanderRadius = 22 + Math.random() * 18
      this.wanderDist = 32 + Math.random() * 18
      this.wanderJitter = 0.14 + Math.random() * 0.16

      // ─── Flutter bob (vertical sine — faster + bigger than firefly) ────
      this.bobPhase = Math.random() * Math.PI * 2
      this.bobFreq = 0.016 + Math.random() * 0.024
      this.bobAmp = 0.28 + Math.random() * 0.44

      // ─── Speed & natural glide cycle ───────────────────────────────────
      this.baseSpeed = 1.1 + Math.random() * 1.3
      this.currentMaxSpeed = this.baseSpeed
      this.glidePhase = Math.random() * Math.PI * 2
      this.glideFreq = 0.007 + Math.random() * 0.011

      // ─── Attraction delay (each butterfly "notices" cursor differently) ─
      this.attractDelay = 10 + Math.floor(Math.random() * 60)
      this.attractDelayTimer = this.attractDelay

      // ─── Wing flap ─────────────────────────────────────────────────────
      this.wingPhase = Math.random() * Math.PI * 2
      this.wingFlapSpeed = 0.14 + Math.random() * 0.22

      // ─── Color variant ─────────────────────────────────────────────────
      this.colorVariant = Math.floor(Math.random() * BUTTERFLY_VARIANTS.length)

      // ─── Landing / perch state ──────────────────────────────────────────
      this.landPhase = 0 // 0=none, 1=approaching, 2=perched, 3=takingOff
      this.landTarget = null
      this.perchedHoverPhase = Math.random() * Math.PI * 2
      // Variable wing speed while perched (organic, non-robotic)
      this.perchFlapSpeed = 0.35 + Math.random() * 0.25
      this.perchFlapTarget = this.perchFlapSpeed
      this.perchFlapTimer = Math.floor(Math.random() * 120)
    } else {
      // ─── Firefly: wander steering (Craig Reynolds style) ───────────────
      this.wanderTheta = Math.random() * Math.PI * 2
      this.wanderRadius = 20 + Math.random() * 18
      this.wanderDist = 40 + Math.random() * 25
      this.wanderJitter = 0.22 + Math.random() * 0.22

      // ─── Hover oscillation (unique per insect) ─────────────────────────
      this.hoverPhase = Math.random() * Math.PI * 2
      this.hoverFreq = 0.007 + Math.random() * 0.013
      this.hoverAmp = 0.15 + Math.random() * 0.28

      // ─── Speed ────────────────────────────────────────────────────────
      this.baseSpeed = 0.45 + Math.random() * 0.65
      this.currentMaxSpeed = this.baseSpeed

      // ─── Attraction delay: each firefly "notices" light at a different time
      this.attractDelay = 20 + Math.floor(Math.random() * 110)
      this.attractDelayTimer = this.attractDelay

      // ─── Glow: organic on/off cycle (each firefly independent timing) ──
      this.glowOnTime = 28 + Math.floor(Math.random() * 75)
      this.glowOffTime = 55 + Math.floor(Math.random() * 220)
      this.glowCycleTimer = Math.floor(Math.random() * (this.glowOnTime + this.glowOffTime))
      this.glowIntensity = 0

      // ─── Subtle hue variation (yellow-green 75° to pure green 100°) ────
      this.glowHue = 75 + Math.random() * 25

      // ─── Light trail ──────────────────────────────────────────────────
      this.trail = []
      this.trailMaxLen = 5 + Math.floor(Math.random() * 7)
    }
  }

  // ─── Firefly: organic glow pulse ──────────────────────────────────────────
  updateGlow() {
    this.glowCycleTimer++
    const cycleLen = this.glowOnTime + this.glowOffTime
    const phase = this.glowCycleTimer % cycleLen

    // On-cycle: bell-curve fade in/out (sin 0→π)
    const target = phase < this.glowOnTime ? Math.sin((phase / this.glowOnTime) * Math.PI) : 0

    // Smooth lerp — slower feels more organic
    this.glowIntensity += (target - this.glowIntensity) * 0.065
    this.glowIntensity = Math.max(0, Math.min(1, this.glowIntensity))
  }

  // ─── Firefly: movement + light attraction ─────────────────────────────────
  updateFirefly() {
    const dx = mouse.x - this.x
    const dy = mouse.y - this.y
    const dist = Math.sqrt(dx * dx + dy * dy) || 1
    const lightActive = mouse.x !== 0 || mouse.y !== 0

    // — Wander steering (Craig Reynolds) —
    this.wanderTheta += (Math.random() - 0.5) * this.wanderJitter

    const spd = Math.sqrt(this.vx * this.vx + this.vy * this.vy) || 1
    const normVx = this.vx / spd
    const normVy = this.vy / spd

    // Project a circle ahead of the insect
    const ccX = this.x + normVx * this.wanderDist
    const ccY = this.y + normVy * this.wanderDist

    // Pick a point on that circle (the "wander target")
    const wtX = ccX + Math.cos(this.wanderTheta) * this.wanderRadius
    const wtY = ccY + Math.sin(this.wanderTheta) * this.wanderRadius

    const wdx = wtX - this.x
    const wdy = wtY - this.y
    const wDist = Math.sqrt(wdx * wdx + wdy * wdy) || 1
    const wanderFx = (wdx / wDist) * 0.048
    const wanderFy = (wdy / wDist) * 0.048

    // — Hover oscillation (gentle vertical sine) —
    this.hoverPhase += this.hoverFreq
    const hoverFy = Math.sin(this.hoverPhase) * this.hoverAmp * 0.022

    // — Light attraction with smooth zones —
    let lightFx = 0
    let lightFy = 0
    let targetMaxSpeed = this.baseSpeed

    if (lightActive) {
      if (this.attractDelayTimer > 0) this.attractDelayTimer--
      const notice = 1 - this.attractDelayTimer / this.attractDelay // 0→1

      if (dist > 300) {
        // Very far: barely a whisper
        lightFx = (dx / dist) * 0.01 * notice
        lightFy = (dy / dist) * 0.01 * notice
      } else if (dist > 85) {
        // Mid-range: smooth pull increasing with proximity
        const t = 1 - (dist - 85) / 215 // 0→1
        const strength = 0.022 + t * 0.11
        lightFx = (dx / dist) * strength * notice
        lightFy = (dy / dist) * strength * notice
        targetMaxSpeed = this.baseSpeed + t * 2.5 * notice
      } else if (dist > 38) {
        // Near: gentle orbit + faint inward drift (no pile-up)
        const tangX = -dy / dist
        const tangY = dx / dist
        const orbitStr = 0.038 * notice
        const inStr = ((dist - 38) / 47) * 0.01 * notice
        lightFx = tangX * orbitStr + (dx / dist) * inStr
        lightFy = tangY * orbitStr + (dy / dist) * inStr
        targetMaxSpeed = this.baseSpeed * 0.72
      } else {
        // Too close: soft repel — never crowd the light source
        lightFx = -(dx / dist) * 0.055
        lightFy = -(dy / dist) * 0.055
        targetMaxSpeed = this.baseSpeed * 0.42
      }
    } else {
      // Light off: gradually forget (restore delay)
      if (this.attractDelayTimer < this.attractDelay) {
        this.attractDelayTimer = Math.min(this.attractDelay, this.attractDelayTimer + 0.4)
      }
    }

    // Smooth speed transition (no instant jumps)
    this.currentMaxSpeed += (targetMaxSpeed - this.currentMaxSpeed) * 0.038

    // Apply all forces
    this.vx += wanderFx + lightFx
    this.vy += wanderFy + lightFy + hoverFy

    // Clamp to max speed
    const newSpd = Math.sqrt(this.vx * this.vx + this.vy * this.vy) || 1
    if (newSpd > this.currentMaxSpeed) {
      this.vx = (this.vx / newSpd) * this.currentMaxSpeed
      this.vy = (this.vy / newSpd) * this.currentMaxSpeed
    }

    // Gentle friction
    this.vx *= 0.972
    this.vy *= 0.972

    // Trail snapshot
    this.trail.push({ x: this.x, y: this.y, g: this.glowIntensity })
    if (this.trail.length > this.trailMaxLen) this.trail.shift()

    // Move
    this.x += this.vx
    this.y += this.vy

    // Wrap
    const m = 60
    if (this.x < -m) this.x = width + m
    if (this.x > width + m) this.x = -m
    if (this.y < -m) this.y = height + m
    if (this.y > height + m) this.y = -m
  }

  // ─── Firefly: draw with trail + multi-layer glow ──────────────────────────
  drawFirefly(c) {
    const g = this.glowIntensity
    const hue = this.glowHue

    // — Trail (absolute coords, oldest → newest) —
    if (g > 0.04 && this.trail.length > 1) {
      for (let i = 0; i < this.trail.length; i++) {
        const age = i / this.trail.length // 0 (oldest) → <1 (most recent)
        const pt = this.trail[i]
        const alpha = pt.g * age * 0.42
        if (alpha < 0.012) continue

        c.save()
        c.shadowBlur = 5
        c.shadowColor = `hsla(${hue}, 90%, 62%, ${alpha * 0.65})`
        c.beginPath()
        c.arc(pt.x, pt.y, 0.5 + age * 1.1, 0, Math.PI * 2)
        c.fillStyle = `hsla(${hue}, 90%, 62%, ${alpha})`
        c.fill()
        c.restore()
      }
    }

    // — Outer diffuse halo (large, very soft) —
    if (g > 0.05) {
      const haloR = 14 + g * 12
      const grad = c.createRadialGradient(this.x, this.y, 0, this.x, this.y, haloR)
      grad.addColorStop(0, `hsla(${hue}, 88%, 64%, ${g * 0.19})`)
      grad.addColorStop(0.45, `hsla(${hue}, 85%, 55%, ${g * 0.07})`)
      grad.addColorStop(1, `hsla(${hue}, 85%, 50%, 0)`)
      c.save()
      c.beginPath()
      c.arc(this.x, this.y, haloR, 0, Math.PI * 2)
      c.fillStyle = grad
      c.fill()
      c.restore()
    }

    // — Pixel body —
    c.save()
    c.translate(this.x, this.y)

    const fireflyColors = {
      1: `rgba(12, 22, 38, ${0.75 + g * 0.25})`,
      2: `rgba(25, 45, 65, ${0.55 + g * 0.2})`,
      3: `hsla(${hue}, 90%, ${40 + Math.round(g * 30)}%, ${0.12 + g * 0.88})`,
    }

    const spriteW = fireflySpriteDesign[0].length * this.size
    const spriteH = fireflySpriteDesign.length * this.size

    c.shadowBlur = g > 0.06 ? 8 + g * 18 : 0
    c.shadowColor = `hsla(${hue}, 90%, 62%, ${g * 0.9})`

    drawSprite(c, -spriteW / 2, -spriteH / 2, this.size, fireflyColors, fireflySpriteDesign)

    // — Inner bright core (appears after 25% intensity) —
    if (g > 0.22) {
      const coreA = Math.min(1, (g - 0.22) * 1.3)
      c.shadowBlur = 0
      c.fillStyle = `hsla(${hue + 14}, 100%, 86%, ${coreA * 0.88})`
      c.fillRect(-1, 1, 3, 3)

      // Tiny pure-white specular at peak glow
      if (g > 0.62) {
        c.fillStyle = `rgba(255, 255, 255, ${Math.min(1, (g - 0.62) * 2.6)})`
        c.fillRect(0, 2, 1, 1)
      }
    }

    c.restore()
  }

  // ─── Butterfly: movement + cursor attraction ──────────────────────────────
  updateButterfly() {
    // ── Landing / perch behaviour ──
    if (this.landPhase === 1) {
      // Approaching target
      if (!this.landTarget) {
        this.landPhase = 0
        return
      }
      const dx = this.landTarget.x - this.x
      const dy = this.landTarget.y - this.y
      const dist = Math.sqrt(dx * dx + dy * dy) || 1
      if (dist < 6) {
        this.landPhase = 2
        this.x = this.landTarget.x
        this.y = this.landTarget.y
        this.vx = 0
        this.vy = 0
      } else {
        const speed = Math.min(3, dist * 0.12)
        this.vx += (dx / dist) * speed * 0.12
        this.vy += (dy / dist) * speed * 0.12
        this.vx *= 0.88
        this.vy *= 0.88
        this.x += this.vx
        this.y += this.vy
      }
      this.wingPhase += this.wingFlapSpeed * 1.3
      this.wingState = Math.sin(this.wingPhase) > 0 ? 'open' : 'closed'
      return
    }

    if (this.landPhase === 2) {
      // Perched — gentle hover near landing spot, variable wing speed
      if (this.landTarget) {
        this.x += (this.landTarget.x - this.x) * 0.25
        this.y += (this.landTarget.y - this.y) * 0.25
        this.perchedHoverPhase += 0.028
        this.y += Math.sin(this.perchedHoverPhase) * 0.35
      }
      // Organic flap speed variation (like real butterflies)
      this.perchFlapTimer++
      if (this.perchFlapTimer > 90 + Math.random() * 160) {
        this.perchFlapTimer = 0
        const r = Math.random()
        if (r < 0.45) this.perchFlapTarget = 0.12 + Math.random() * 0.2
        else if (r < 0.78) this.perchFlapTarget = 0.32 + Math.random() * 0.28
        else this.perchFlapTarget = 0.65 + Math.random() * 0.45
      }
      this.perchFlapSpeed += (this.perchFlapTarget - this.perchFlapSpeed) * 0.05
      this.wingPhase += this.wingFlapSpeed * this.perchFlapSpeed
      this.wingState = Math.sin(this.wingPhase) > 0 ? 'open' : 'closed'
      return
    }

    if (this.landPhase === 3) {
      // Taking off — one-frame burst, then resume normal flight
      this.landPhase = 0
      this.landTarget = null
      this.x += this.vx
      this.y += this.vy
      this.wingPhase += this.wingFlapSpeed * 2.2
      this.wingState = Math.sin(this.wingPhase) > 0 ? 'open' : 'closed'
      return
    }

    // — Wander steering (Craig Reynolds) —
    this.wanderTheta += (Math.random() - 0.5) * this.wanderJitter

    const spd = Math.sqrt(this.vx * this.vx + this.vy * this.vy) || 1
    const normVx = this.vx / spd
    const normVy = this.vy / spd

    const ccX = this.x + normVx * this.wanderDist
    const ccY = this.y + normVy * this.wanderDist
    const wtX = ccX + Math.cos(this.wanderTheta) * this.wanderRadius
    const wtY = ccY + Math.sin(this.wanderTheta) * this.wanderRadius

    const wdx = wtX - this.x
    const wdy = wtY - this.y
    const wDist = Math.sqrt(wdx * wdx + wdy * wdy) || 1
    const wanderFx = (wdx / wDist) * 0.06
    const wanderFy = (wdy / wDist) * 0.06

    // — Flutter bob —
    this.bobPhase += this.bobFreq
    const bobFy = Math.sin(this.bobPhase) * this.bobAmp * 0.024

    // — Glide cycle: natural speed modulation —
    this.glidePhase += this.glideFreq
    const glide = 0.72 + Math.sin(this.glidePhase) * 0.28

    // — Cursor attraction with smooth zones —
    const dx = mouse.x - this.x
    const dy = mouse.y - this.y
    const dist = Math.sqrt(dx * dx + dy * dy) || 1
    const cursorActive = mouse.x !== 0 || mouse.y !== 0

    let attractFx = 0
    let attractFy = 0
    let targetMaxSpeed = this.baseSpeed * glide

    if (cursorActive) {
      if (this.attractDelayTimer > 0) this.attractDelayTimer--
      const notice = 1 - this.attractDelayTimer / this.attractDelay

      if (dist > 280) {
        attractFx = (dx / dist) * 0.01 * notice
        attractFy = (dy / dist) * 0.01 * notice
      } else if (dist > 75) {
        const t = 1 - (dist - 75) / 205
        const strength = 0.022 + t * 0.085
        attractFx = (dx / dist) * strength * notice
        attractFy = (dy / dist) * strength * notice
        targetMaxSpeed = (this.baseSpeed + t * 1.8 * notice) * glide
      } else if (dist > 32) {
        const tangX = -dy / dist
        const tangY = dx / dist
        attractFx = tangX * 0.035 * notice + (dx / dist) * 0.008 * notice
        attractFy = tangY * 0.035 * notice + (dy / dist) * 0.008 * notice
        targetMaxSpeed = this.baseSpeed * 0.6 * glide
      } else {
        // Too close: soft repel
        attractFx = -(dx / dist) * 0.055
        attractFy = -(dy / dist) * 0.055
        targetMaxSpeed = this.baseSpeed * 0.35
      }
    } else {
      if (this.attractDelayTimer < this.attractDelay) {
        this.attractDelayTimer = Math.min(this.attractDelay, this.attractDelayTimer + 0.5)
      }
    }

    this.currentMaxSpeed += (targetMaxSpeed - this.currentMaxSpeed) * 0.04

    this.vx += wanderFx + attractFx
    this.vy += wanderFy + attractFy + bobFy

    const newSpd = Math.sqrt(this.vx * this.vx + this.vy * this.vy) || 1
    if (newSpd > this.currentMaxSpeed) {
      this.vx = (this.vx / newSpd) * this.currentMaxSpeed
      this.vy = (this.vy / newSpd) * this.currentMaxSpeed
    }

    this.vx *= 0.965
    this.vy *= 0.965

    this.x += this.vx
    this.y += this.vy

    const m = 60
    if (this.x < -m) this.x = width + m
    if (this.x > width + m) this.x = -m
    if (this.y < -m) this.y = height + m
    if (this.y > height + m) this.y = -m

    // Wing flap speed tied to movement (faster flight = faster flapping)
    this.wingPhase += this.wingFlapSpeed * Math.max(0.5, newSpd / this.baseSpeed)
    this.wingState = Math.sin(this.wingPhase) > 0 ? 'open' : 'closed'
  }

  // ─── Landing / perch control ─────────────────────────────────────────────
  startLanding(tx, ty) {
    this.landTarget = { x: tx, y: ty }
    this.landPhase = 1
  }

  takeOff() {
    if (this.landPhase === 0) return
    this.landPhase = 3
    // Burst away from perch point
    const dx = this.x - mouse.x
    const dy = this.y - mouse.y
    const dist = Math.sqrt(dx * dx + dy * dy) || 1
    this.vx = (dx / dist) * 3.5 + (Math.random() - 0.5) * 2
    this.vy = (dy / dist) * 2.2 - 1.8 + (Math.random() - 0.5) * 1.5
  }

  // ─── Butterfly: draw with shadow + color variant ──────────────────────────
  drawButterfly(c) {
    const sprite = this.wingState === 'open' ? butterflyOpen : butterflyClosed
    const colors = BUTTERFLY_VARIANTS[this.colorVariant]
    const spd = Math.sqrt(this.vx * this.vx + this.vy * this.vy)
    const tilt = Math.atan2(this.vy, this.vx) * 0.15

    // Soft ellipse shadow — grows slightly with speed
    c.save()
    c.globalAlpha = 0.09 + Math.min(0.07, spd * 0.02)
    c.fillStyle = '#000'
    c.beginPath()
    c.ellipse(
      this.x + 2,
      this.y + this.size * 4,
      this.size * 2.8,
      this.size * 1.1,
      0,
      0,
      Math.PI * 2
    )
    c.fill()
    c.globalAlpha = 1
    c.restore()

    // Sprite with direction-based tilt
    c.save()
    c.translate(this.x, this.y)
    c.rotate(tilt)
    c.translate(-(sprite[0].length * this.size) / 2, -(sprite.length * this.size) / 2)
    drawSprite(c, 0, 0, this.size, colors, sprite)
    c.restore()
  }

  update() {
    if (isDay.value) {
      this.updateButterfly()
    } else {
      // ─── Firefly ───────────────────────────────────────────────────────
      this.updateGlow()
      this.updateFirefly()
    }
  }

  draw(c) {
    if (isDay.value) {
      this.drawButterfly(c)
    } else {
      this.drawFirefly(c)
    }
  }
}

/* ===========================
   NIGHT BG ELEMENTS
   =========================== */
class PixelStar {
  constructor() {
    this.init()
  }
  init() {
    this.x = Math.floor(Math.random() * width)
    this.y = Math.floor(Math.random() * height)
    this.size = Math.random() < 0.8 ? 2 : 3
    this.baseAlpha = Math.random() * 0.4 + 0.2
    this.alpha = this.baseAlpha
    this.blinkSpeed = Math.random() * 0.003 + 0.001
    this.blinkDir = 1
  }
  update() {
    if (this.blinkDir === 1) {
      this.alpha += this.blinkSpeed
      if (this.alpha >= this.baseAlpha + 0.15) this.blinkDir = -1
    } else {
      this.alpha -= this.blinkSpeed
      if (this.alpha <= this.baseAlpha - 0.15) this.blinkDir = 1
    }
    this.alpha = Math.max(0.1, Math.min(0.8, this.alpha))
  }
  draw() {
    ctx.fillStyle = `rgba(255, 255, 255, ${this.alpha})`
    ctx.fillRect(this.x, this.y, this.size, this.size)
  }
}

class ShootingStar {
  constructor() {
    this.reset()
  }
  reset() {
    this.x = Math.random() * width
    this.y = Math.random() * (height * 0.7)
    this.len = Math.random() * 60 + 20
    this.speed = Math.random() * 4 + 2
    this.size = 2
    this.angle = 25 + Math.random() * 15
    this.life = 0
    this.maxLife = 80
    this.opacity = 0
  }
  update() {
    this.x += this.speed * Math.cos((this.angle * Math.PI) / 180)
    this.y += this.speed * Math.sin((this.angle * Math.PI) / 180)
    this.life++
    if (this.life < 10) this.opacity += 0.1
    else if (this.life > this.maxLife - 15) this.opacity -= 0.1
    if (this.x > width || this.y > height || this.opacity <= 0) return false
    return true
  }
  draw() {
    const tailX = this.x - this.len * Math.cos((this.angle * Math.PI) / 180)
    const tailY = this.y - this.len * Math.sin((this.angle * Math.PI) / 180)
    const gradient = ctx.createLinearGradient(this.x, this.y, tailX, tailY)
    gradient.addColorStop(0, `rgba(255, 255, 255, ${this.opacity})`)
    gradient.addColorStop(1, `rgba(255, 255, 255, 0)`)
    ctx.lineWidth = this.size
    ctx.strokeStyle = gradient
    ctx.beginPath()
    ctx.moveTo(Math.floor(this.x), Math.floor(this.y))
    ctx.lineTo(Math.floor(tailX), Math.floor(tailY))
    ctx.stroke()
    ctx.fillStyle = `rgba(255, 255, 255, ${this.opacity})`
    ctx.fillRect(Math.floor(this.x), Math.floor(this.y), this.size, this.size)
  }
}

/* ===========================
   DAY BG ELEMENTS
   =========================== */
class PixelCloud {
  constructor(depth = 'mid') {
    this.depth = depth
    this.reset()
    this.x = Math.random() * width
    this.fadeIn = 1 // clouds placed at init start fully visible
  }
  reset() {
    this.x = -700 - Math.random() * 500
    this.fadeIn = 0
    if (this.depth === 'far') {
      this.y = Math.random() * (height * 0.22)
      this.speed = Math.random() * 0.07 + 0.025
      this.scale = Math.floor(Math.random() * 2 + 1)
      this.opacity = Math.random() * 0.1 + 0.11
    } else if (this.depth === 'near') {
      this.y = Math.random() * (height * 0.35)
      this.speed = Math.random() * 0.42 + 0.25
      this.scale = Math.floor(Math.random() * 3 + 5)
      this.opacity = Math.random() * 0.2 + 0.55
    } else if (this.depth === 'fg') {
      // Foreground layer — rendered above text; semi-transparent so text stays readable
      this.y = Math.random() * (height * 0.55)
      this.speed = Math.random() * 0.3 + 0.18
      this.scale = Math.floor(Math.random() * 3 + 4)
      this.opacity = Math.random() * 0.15 + 0.32
    } else {
      // mid
      this.y = Math.random() * (height * 0.3)
      this.speed = Math.random() * 0.17 + 0.08
      this.scale = Math.floor(Math.random() * 3 + 3)
      this.opacity = Math.random() * 0.18 + 0.32
    }
    this.variant = Math.floor(Math.random() * 6)
    this.s = Array.from({ length: 8 }, () => Math.random())
    // Vertical bob — near/fg clouds bob slightly more than far ones
    this.bobPhase = Math.random() * Math.PI * 2
    this.bobFreq = 0.004 + Math.random() * 0.006
    this.bobAmp =
      this.depth === 'near' || this.depth === 'fg'
        ? 0.55 + Math.random() * 0.5
        : 0.18 + Math.random() * 0.28
  }
  update() {
    this.x += this.speed
    this.bobPhase += this.bobFreq
    // Fade in as the cloud drifts into view from the left edge
    if (this.fadeIn < 1) this.fadeIn = Math.min(1, Math.max(0, (this.x + 160) / 160))
    if (this.x > width + 750) this.reset()
  }

  // Rounded pixel-puff: staggered rows approximating an ellipse
  _puff(cx, cy, rx, ry, color) {
    ctx.fillStyle = color
    const rows = ry * 2 + 1
    for (let r = 0; r < rows; r++) {
      const dy = r - ry
      const ratio = Math.sqrt(Math.max(0, 1 - (dy * dy) / (ry * ry || 1)))
      const w = Math.max(1, Math.floor(rx * 2 * ratio))
      ctx.fillRect(cx - Math.floor(w / 2), cy + dy, w, 1)
    }
  }

  // Flat-bottomed puff for shadow bases
  _flatPuff(cx, cy, rx, ry, color) {
    ctx.fillStyle = color
    for (let r = 0; r <= ry; r++) {
      const dy = r - ry
      const ratio = Math.sqrt(Math.max(0, 1 - (dy * dy) / (ry * ry || 1)))
      const w = Math.max(1, Math.floor(rx * 2 * ratio))
      ctx.fillRect(cx - Math.floor(w / 2), cy + dy, w, 1)
    }
  }

  draw() {
    const bobY = Math.sin(this.bobPhase) * this.bobAmp
    ctx.save()
    ctx.translate(Math.floor(this.x), Math.floor(this.y + bobY))
    ctx.scale(this.scale, this.scale)

    const a = this.opacity * this.fadeIn
    const s = this.s
    const body = `rgba(250, 253, 255, ${a})`
    const bodyWarm = `rgba(252, 250, 246, ${a * 0.92})`
    const mid = `rgba(228, 238, 252, ${a * 0.75})`
    const shadow = `rgba(188, 202, 222, ${a * 0.48})`
    const deepShadow = `rgba(165, 182, 210, ${a * 0.3})`
    const highlight = `rgba(255, 255, 255, ${a * 0.92})`
    const warmHL = `rgba(255, 252, 244, ${a * 0.85})`
    const underbelly = `rgba(238, 226, 212, ${a * 0.22})`

    if (this.variant === 0) {
      // ── Fluffy cumulus: wide base, abundant bumps ──
      const bx = 24,
        by = 18
      this._flatPuff(bx, by + 7, 16 + s[0] * 3, 3, deepShadow)
      this._flatPuff(bx - 6, by + 5, 11, 3, shadow)
      this._flatPuff(bx + 9, by + 5, 11, 3, shadow)
      this._flatPuff(bx, by + 9, 14 + s[1] * 3, 2, underbelly)
      this._puff(bx, by, 17 + s[0] * 4, 11 + s[1] * 3, body)
      this._puff(bx - 13 + s[2] * 4, by - 3, 10 + s[3] * 3, 9 + s[4] * 2, body)
      this._puff(bx - 21 + s[5] * 3, by + 2, 8, 7, mid)
      this._puff(bx + 14 + s[3] * 3, by - 3, 11 + s[5] * 3, 8 + s[0] * 2, body)
      this._puff(bx + 23 + s[1] * 2, by + 2, 7, 6, mid)
      this._puff(bx + 29 + s[6] * 2, by + 4, 5, 4, mid)
      this._puff(bx - 5 + s[4] * 4, by - 10, 9, 6, highlight)
      this._puff(bx + 10 + s[2] * 3, by - 8, 7, 5, warmHL)
      this._puff(bx + 2 + s[7] * 3, by - 13, 5, 4, highlight)
    } else if (this.variant === 1) {
      // ── Towering cumulus congestus ──
      const bx = 16,
        by = 44
      this._flatPuff(bx, by, 12 + s[0] * 2, 2, deepShadow)
      this._flatPuff(bx, by + 2, 10 + s[1] * 2, 2, underbelly)
      this._puff(bx + s[1] * 3, by - 9, 15 + s[2] * 3, 10 + s[3] * 2, body)
      this._puff(bx - 3 + s[4] * 4, by - 20, 12 + s[5] * 2, 9 + s[0] * 2, body)
      this._puff(bx + 6 + s[2] * 2, by - 18, 8, 7, mid)
      this._puff(bx + s[3] * 3, by - 31, 10 + s[1] * 2, 8 + s[4] * 2, body)
      this._puff(bx - 4 + s[6] * 3, by - 28, 6, 5, mid)
      this._puff(bx + 1 + s[5] * 3, by - 40, 7, 6, highlight)
      this._puff(bx + 4 + s[0] * 2, by - 38, 5, 4, warmHL)
      this._puff(bx - 2 + s[7] * 2, by - 44, 4, 3, highlight)
    } else if (this.variant === 2) {
      // ── Layered stratus: wide, flat, three offset layers ──
      const bx = 32,
        by = 11
      this._flatPuff(bx + s[0] * 4, by + 9, 33 + s[1] * 4, 2, deepShadow)
      this._flatPuff(bx + 6, by + 7, 28, 3, shadow)
      this._flatPuff(bx + 8, by + 10, 24, 2, underbelly)
      this._puff(bx + s[2] * 5, by + 2, 30 + s[3] * 5, 7 + s[4] * 2, body)
      this._puff(bx - 5 + s[5] * 5, by - 4, 26 + s[0] * 4, 6 + s[1] * 2, bodyWarm)
      this._puff(bx - 12 + s[2] * 4, by - 9, 22 + s[3] * 3, 5 + s[4] * 2, body)
      this._puff(bx - 3 + s[5] * 3, by + 1, 20, 3, highlight)
      this._puff(bx - 10 + s[0] * 3, by - 5, 16, 2, warmHL)
      this._puff(bx + 4 + s[7] * 3, by - 8, 10, 2, highlight)
    } else if (this.variant === 3) {
      // ── Compact puff cluster ──
      const bx = 20,
        by = 13
      this._flatPuff(bx + s[0] * 2, by + 5, 17 + s[1] * 3, 2, deepShadow)
      this._flatPuff(bx, by + 7, 13 + s[2] * 2, 2, underbelly)
      this._puff(bx + s[2] * 3, by, 12 + s[3] * 3, 8 + s[4] * 2, body)
      this._puff(bx - 9 + s[5] * 4, by + 2, 9 + s[0] * 2, 7 + s[1] * 2, body)
      this._puff(bx + 11 + s[2] * 3, by + 1, 9 + s[3] * 2, 6 + s[4], mid)
      this._puff(bx - 1 + s[5] * 3, by - 6, 8, 5, highlight)
      this._puff(bx + 8 + s[0] * 2, by - 4, 6, 4, warmHL)
      this._puff(bx - 7 + s[7] * 2, by - 2, 5, 3, mid)
    } else if (this.variant === 4) {
      // ── Wispy cirrus / alto-cumulus ──
      const bx = 28,
        by = 7
      this._puff(bx + s[0] * 6, by, 22 + s[1] * 8, 4 + s[2] * 2, mid)
      this._puff(bx - 8 + s[3] * 5, by + 2, 16 + s[4] * 5, 3, body)
      this._puff(bx + 18 + s[5] * 4, by - 1, 14 + s[6] * 5, 3, mid)
      this._puff(bx + 4 + s[7] * 4, by - 3, 18 + s[0] * 4, 2, highlight)
      this._puff(bx - 16 + s[1] * 4, by + 4, 10 + s[2] * 4, 2, `rgba(230, 240, 255, ${a * 0.5})`)
      this._puff(bx + 28 + s[3] * 5, by + 3, 12 + s[4] * 4, 2, `rgba(230, 240, 255, ${a * 0.5})`)
    } else {
      // ── Asymmetric multi-tower ──
      const bx = 18,
        by = 28
      this._flatPuff(bx + 4, by + 2, 18 + s[0] * 3, 3, deepShadow)
      this._flatPuff(bx + 5, by + 4, 14 + s[1] * 2, 2, underbelly)
      this._puff(bx + s[2] * 4, by - 4, 19 + s[3] * 4, 8 + s[4] * 2, body)
      this._puff(bx - 2 + s[5] * 3, by - 16, 10 + s[6] * 2, 9 + s[0] * 2, body)
      this._puff(bx - 4 + s[7] * 3, by - 26, 8 + s[1] * 2, 7 + s[2] * 2, bodyWarm)
      this._puff(bx - 2 + s[3] * 2, by - 34, 6, 5, highlight)
      this._puff(bx + 12 + s[4] * 3, by - 13, 9 + s[5] * 2, 7 + s[6] * 2, body)
      this._puff(bx + 14 + s[7] * 2, by - 21, 7, 6, mid)
      this._puff(bx + 10 + s[0] * 2, by - 28, 5, 4, warmHL)
    }

    ctx.restore()
  }
}

/* ===========================
   PHYSICS LEAF (Day BG)
   =========================== */
class PhysicsLeaf {
  constructor(x, y) {
    this.x = x
    this.y = y

    // Two distinct fall modes for visual variety
    // 'zigzag'  (~45%): very light, wide pendulum swing — like a leaf caught in a gentle breeze
    // 'drift'   (~55%): moderate weight, soft flutter — steady but not perfectly straight
    this.mode = Math.random() < 0.45 ? 'zigzag' : 'drift'

    if (this.mode === 'zigzag') {
      this.vy = 0.06 + Math.random() * 0.16 // very gentle initial fall
      this.vx = (Math.random() - 0.5) * 0.4
      this.gravity = 0.007 + Math.random() * 0.013 // 0.007–0.020 — floats
      this.windDrift = (Math.random() - 0.5) * 0.005
      this.angularVel = (Math.random() - 0.5) * 0.06
      this.flutterFreq = 0.026 + Math.random() * 0.022 // slow cycle → wide arc
      this.flutterAmp = 1.0 + Math.random() * 0.9 // 1.0–1.9 — strong swing
      this.flutterXContrib = 1.5 + Math.random() * 1.0 // 1.5–2.5 — big lateral push
    } else {
      this.vy = 0.1 + Math.random() * 0.25 // gentle but steady fall
      this.vx = (Math.random() - 0.5) * 0.6
      this.gravity = 0.014 + Math.random() * 0.016 // 0.014–0.030 — lighter than before
      this.windDrift = (Math.random() - 0.5) * 0.008
      this.angularVel = (Math.random() - 0.5) * 0.1
      this.flutterFreq = 0.044 + Math.random() * 0.042 // faster cycle → tighter sway
      this.flutterAmp = 0.16 + Math.random() * 0.32 // 0.16–0.48 — subtle flutter
      this.flutterXContrib = 0.22 + Math.random() * 0.32 // 0.22–0.54
    }

    this.angle = Math.random() * Math.PI * 2
    this.size = Math.random() < 0.28 ? 3 : 2
    this.alpha = 0
    this.flutter = Math.random() * Math.PI * 2
    this.dead = false
    this.life = 0
    this.maxLife = 290 + Math.floor(Math.random() * 320)

    // Palette chosen at spawn — vivid seasonal tones by day, moonlit shadows at night
    const palettes = isDay.value
      ? [
          ['#2d6a2d', '#4a8c3f'], // deep green
          ['#4a8c3f', '#68b04a'], // green
          ['#85b52a', '#a8d43e'], // lime
          ['#c8a000', '#e6c820'], // golden
          ['#d4820a', '#f09822'], // amber
          ['#b85418', '#d46828'], // orange-rust
          ['#8b3210', '#aa4420'], // rust
        ]
      : [
          ['#1a3525', '#263d2e'], // moonlit green
          ['#152d22', '#1f3d2c'], // deep forest
          ['#0f2218', '#1a3020'], // shadow green
          ['#1c3028', '#283c34'], // cool teal-green
          ['#203828', '#2d4a36'], // faint moonlit
          ['#1a2820', '#26382c'], // muted grey-green
          ['#162a1e', '#224030'], // deep moonlit
        ]
    const p = palettes[Math.floor(Math.random() * palettes.length)]
    this.colorDark = p[0]
    this.colorLight = p[1]
  }

  update() {
    this.life++
    // Flutter oscillation drives the characteristic side-to-side fall
    this.flutter += this.flutterFreq
    const fl = Math.sin(this.flutter) * this.flutterAmp

    this.vy += this.gravity
    this.vx += this.windDrift + fl * 0.025

    // Zigzag leaves hold their swing longer; drifters bleed off speed faster
    if (this.mode === 'zigzag') {
      this.vx *= 0.993
      this.vy *= 0.998
    } else {
      this.vx *= 0.987
      this.vy *= 0.993
    }

    // Spin follows flutter — leaf tumbles more when fluttering
    this.angularVel += fl * 0.003 + (Math.random() - 0.5) * 0.003
    this.angularVel *= 0.962
    this.angle += this.angularVel

    // flutterXContrib drives the visible lateral displacement: high for zigzag, low for drift
    this.x += this.vx + fl * this.flutterXContrib
    this.y += this.vy

    // Opacity: fade in, cruise, fade out
    if (this.life < 22) this.alpha = this.life / 22
    else if (this.life > this.maxLife - 48)
      this.alpha = Math.max(0, (this.maxLife - this.life) / 48)
    else this.alpha = 0.82 + Math.random() * 0.18

    if (this.y > height + 60 || this.x < -80 || this.x > width + 80 || this.life >= this.maxLife) {
      this.dead = true
    }
  }

  draw() {
    ctx.save()
    ctx.globalAlpha = this.alpha * 0.88
    ctx.translate(Math.round(this.x), Math.round(this.y))
    ctx.rotate(this.angle)

    const s = this.size
    // Pixel leaf body — compact oval
    ctx.fillStyle = this.colorDark
    ctx.fillRect(-s, -s, s * 2, s * 3) // main body
    ctx.fillRect(-Math.floor(s * 0.5), -s * 2, s, s) // top tip
    // Lit (highlight) edge
    ctx.fillStyle = this.colorLight
    ctx.fillRect(-s, -s, s, s) // lit corner
    ctx.fillRect(-Math.floor(s * 0.5), -s * 2, s, Math.floor(s * 0.5)) // tip highlight

    ctx.globalAlpha = 1
    ctx.restore()
  }
}

// --- INIT FUNCTIONS ---
const initInsects = () => {
  insects = []
  const count = getInsectCount()
  const fgCount = getFgCount()
  for (let i = 0; i < count; i++) insects.push(new Insect())

  // First DEEP_BG_COUNT insects go behind the text layer
  for (let i = 0; i < DEEP_BG_COUNT; i++) {
    insects[i].isDeepBg = true
  }

  // Foreground insects: avoid the deepBg slots
  const fgIndices = new Set()
  while (fgIndices.size < fgCount) {
    const idx = Math.floor(Math.random() * count)
    if (!insects[idx].isDeepBg) fgIndices.add(idx)
  }
  fgIndices.forEach((i) => {
    insects[i].isForeground = true
  })
}
const initStars = () => {
  stars = []
  for (let i = 0; i < STAR_COUNT; i++) stars.push(new PixelStar())
}
const initClouds = () => {
  clouds = []
  for (let i = 0; i < 4; i++) clouds.push(new PixelCloud('far')) // distant haze
  for (let i = 0; i < 7; i++) clouds.push(new PixelCloud('mid')) // mid layer
  for (let i = 0; i < 5; i++) clouds.push(new PixelCloud('near')) // near layer
}
const initFgClouds = () => {
  fgClouds = []
  // 3 semi-transparent clouds that drift above the text
  for (let i = 0; i < 3; i++) fgClouds.push(new PixelCloud('fg'))
}

const initCanopies = () => {
  canopies = []
  for (const side of ['left', 'right']) {
    // 3 large background trees (tall, slow sway)
    for (let i = 0; i < 3; i++) {
      canopies.push({
        side,
        y: Math.random() * height * 0.65 + height * 0.04,
        scale: Math.random() * 2.0 + 5.5,
        opacity: Math.random() * 0.12 + 0.52,
        swayPhase: Math.random() * Math.PI * 2,
        swaySpeed: Math.random() * 0.003 + 0.0015,
        swayAmp: Math.random() * 2 + 1.5,
        template: Math.floor(Math.random() * CANOPY_TEMPLATES.length),
      })
    }
    // 4 medium mid-ground trees
    for (let i = 0; i < 4; i++) {
      canopies.push({
        side,
        y: Math.random() * height * 0.78 + height * 0.06,
        scale: Math.random() * 1.8 + 3.2,
        opacity: Math.random() * 0.15 + 0.62,
        swayPhase: Math.random() * Math.PI * 2,
        swaySpeed: Math.random() * 0.004 + 0.0025,
        swayAmp: Math.random() * 3 + 2,
        template: Math.floor(Math.random() * CANOPY_TEMPLATES.length),
      })
    }
    // 2 small foreground accent shrubs (faster sway)
    for (let i = 0; i < 2; i++) {
      canopies.push({
        side,
        y: Math.random() * height * 0.8 + height * 0.1,
        scale: Math.random() * 1.2 + 1.8,
        opacity: Math.random() * 0.2 + 0.68,
        swayPhase: Math.random() * Math.PI * 2,
        swaySpeed: Math.random() * 0.006 + 0.004,
        swayAmp: Math.random() * 4 + 3,
        template: Math.floor(Math.random() * CANOPY_TEMPLATES.length),
      })
    }
  }
}

const startAnimating = () => {
  if (isAnimating) return
  isAnimating = true
  animate()
}

const stopAnimating = () => {
  isAnimating = false
  if (animationFrameId) {
    cancelAnimationFrame(animationFrameId)
    animationFrameId = null
  }
}

watch(
  () => props.visible,
  (val) => {
    if (val) startAnimating()
    else stopAnimating()
  }
)

const animate = () => {
  if (!isAnimating) return
  ctx.clearRect(0, 0, width, height)

  // 1. Draw Background Elements first

  // ── Tree canopies (edges, behind everything — day + night) ──
  for (const canopy of canopies) {
    canopy.swayPhase += canopy.swaySpeed
    const sway = Math.sin(canopy.swayPhase) * canopy.swayAmp
    const baseX = canopy.side === 'left' ? -20 : width + 20
    const x = baseX + sway

    ctx.save()
    ctx.translate(Math.floor(x), Math.floor(canopy.y))
    ctx.scale(canopy.scale, canopy.scale)

    // sunDir: +1 = light from right (left-side trees), -1 = light from left (right-side trees)
    const sunDir = canopy.side === 'left' ? 1 : -1
    let shadow, dark, mid, light, hl, glow
    if (isDay.value) {
      shadow = `rgba(14, 42, 28, ${canopy.opacity * 0.8})`
      dark = `rgba(28, 72, 52, ${canopy.opacity * 0.7})`
      mid = `rgba(52, 121, 88, ${canopy.opacity})`
      light = `rgba(88, 190, 140, ${canopy.opacity * 0.72})`
      hl = `rgba(130, 218, 170, ${canopy.opacity * 0.52})`
      glow = `rgba(165, 235, 195, ${canopy.opacity * 0.22})`
    } else {
      shadow = `rgba(2, 6, 4, ${canopy.opacity * 0.98})`
      dark = `rgba(7, 18, 13, ${canopy.opacity * 0.95})`
      mid = `rgba(13, 30, 22, ${canopy.opacity * 0.9})`
      light = `rgba(20, 48, 35, ${canopy.opacity * 0.55})`
      hl = `rgba(28, 62, 46, ${canopy.opacity * 0.38})`
      glow = `rgba(160, 190, 220, ${canopy.opacity * 0.2})`
    }

    const tpl = CANOPY_TEMPLATES[canopy.template]
    for (const puff of tpl) {
      const flipX = canopy.side === 'left' ? 1 : -1
      const px = puff.ox * flipX
      // 1. Drop shadow — offset to shadow side and slightly down
      drawPuff(px - sunDir * puff.rx * 0.15, puff.oy + 3, puff.rx * 0.92, puff.ry * 0.58, shadow)
      // 2. Dark base
      drawPuff(px, puff.oy, puff.rx, puff.ry, dark)
      // 3. Mid tone, shifted toward lit side
      drawPuff(
        px + sunDir * puff.rx * 0.1,
        puff.oy - puff.ry * 0.1,
        puff.rx * 0.82,
        puff.ry * 0.85,
        mid
      )
      // 4. Light face on the lit side
      drawPuff(
        px + sunDir * puff.rx * 0.26,
        puff.oy - puff.ry * 0.3,
        puff.rx * 0.5,
        puff.ry * 0.4,
        light
      )
      // 5. Specular highlight — upper lit corner
      drawPuff(
        px + sunDir * puff.rx * 0.32,
        puff.oy - puff.ry * 0.52,
        puff.rx * 0.26,
        puff.ry * 0.22,
        hl
      )
      // 6. Top glow / moonlight rim
      drawPuff(
        px + sunDir * puff.rx * 0.18,
        puff.oy - puff.ry * 0.68,
        puff.rx * 0.16,
        puff.ry * 0.14,
        glow
      )
    }

    ctx.restore()
  }

  if (isDay.value) {
    // ── Pixel sun ──
    const sunX = width * 0.82
    const sunY = height * 0.14
    const sunR = Math.floor(Math.min(width, height) * 0.065)
    const ps = 3

    // Multi-layer atmospheric glow
    const sunGlow = ctx.createRadialGradient(sunX, sunY, sunR * 0.4, sunX, sunY, sunR * 3.5)
    sunGlow.addColorStop(0, 'rgba(255, 255, 220, 0.65)')
    sunGlow.addColorStop(0.18, 'rgba(255, 240, 160, 0.45)')
    sunGlow.addColorStop(0.4, 'rgba(255, 210, 80, 0.18)')
    sunGlow.addColorStop(0.7, 'rgba(255, 180, 40, 0.06)')
    sunGlow.addColorStop(1, 'rgba(255, 150, 0, 0)')
    ctx.fillStyle = sunGlow
    ctx.beginPath()
    ctx.arc(sunX, sunY, sunR * 3.5, 0, Math.PI * 2)
    ctx.fill()

    // Pixel corona rays — 8 directions, tapering away from the body
    const rayDegrees = [0, 45, 90, 135, 180, 225, 270, 315]
    for (let i = 0; i < rayDegrees.length; i++) {
      const angle = (rayDegrees[i] * Math.PI) / 180
      const isDiag = i % 2 === 1
      const maxLen = isDiag ? sunR * 0.4 : sunR * 0.55
      for (let dist = ps; dist <= maxLen; dist += ps) {
        const t = dist / maxLen
        const rx = Math.floor(sunX + Math.cos(angle) * (sunR + dist))
        const ry = Math.floor(sunY + Math.sin(angle) * (sunR + dist))
        const size = Math.max(ps, Math.floor(ps * (1 - t * 0.55)))
        ctx.fillStyle = `rgba(255, ${Math.floor(210 - t * 90)}, ${Math.floor(50 - t * 35)}, ${(1 - t) * 0.58})`
        ctx.fillRect(rx - Math.floor(size / 2), ry - Math.floor(size / 2), size, size)
      }
    }

    // Pixel body — 6-band gradient with limb darkening at the edges
    for (let dy = -sunR; dy <= sunR; dy += ps) {
      for (let dx = -sunR; dx <= sunR; dx += ps) {
        const d2 = dx * dx + dy * dy
        if (d2 <= sunR * sunR) {
          const d = Math.sqrt(d2) / sunR
          if (d < 0.14) ctx.fillStyle = '#FFFFFF'
          else if (d < 0.3) ctx.fillStyle = '#FFFCE8'
          else if (d < 0.5) ctx.fillStyle = '#FFE57A'
          else if (d < 0.68) ctx.fillStyle = '#FFCC28'
          else if (d < 0.84) ctx.fillStyle = '#FFB300'
          else ctx.fillStyle = '#FF8F00'
          ctx.fillRect(Math.floor(sunX + dx), Math.floor(sunY + dy), ps, ps)
        }
      }
    }

    // Sunspots — small darker patches on the surface
    const sunspots = [
      { ox: sunR * 0.22, oy: -sunR * 0.14, r: sunR * 0.1 },
      { ox: -sunR * 0.28, oy: sunR * 0.12, r: sunR * 0.08 },
      { ox: sunR * 0.1, oy: sunR * 0.32, r: sunR * 0.065 },
    ]
    for (const sp of sunspots) {
      for (let dy = -sp.r; dy <= sp.r; dy += ps) {
        for (let dx = -sp.r; dx <= sp.r; dx += ps) {
          if (dx * dx + dy * dy <= sp.r * sp.r) {
            const d = Math.sqrt(dx * dx + dy * dy) / sp.r
            ctx.fillStyle = d < 0.5 ? '#B06000' : '#CC7800'
            ctx.fillRect(Math.floor(sunX + sp.ox + dx), Math.floor(sunY + sp.oy + dy), ps, ps)
          }
        }
      }
    }

    clouds.forEach((c) => {
      c.update()
      c.draw()
    })
  } else {
    // ── Pixel moon (real phase) ──
    const moonX = width * 0.16
    const moonY = height * 0.12
    const moonR = Math.floor(Math.min(width, height) * 0.05)
    const mps = 3
    const moonPhase = getMoonPhase()
    const darkOx = moonDarkOffset(moonPhase, moonR)

    // Atmospheric glow — peaks at full, fades to nothing at new moon
    const glowBright = Math.max(0, 0.5 - Math.abs(moonPhase - 0.5) * 0.9)
    if (glowBright > 0.01) {
      const mg = ctx.createRadialGradient(moonX, moonY, moonR * 0.5, moonX, moonY, moonR * 3.2)
      mg.addColorStop(0, `rgba(228, 240, 255, ${glowBright})`)
      mg.addColorStop(0.28, `rgba(200, 218, 252, ${glowBright * 0.5})`)
      mg.addColorStop(0.62, `rgba(165, 190, 238, ${glowBright * 0.14})`)
      mg.addColorStop(1, 'rgba(128, 155, 220, 0)')
      ctx.fillStyle = mg
      ctx.beginPath()
      ctx.arc(moonX, moonY, moonR * 3.2, 0, Math.PI * 2)
      ctx.fill()
    }

    // Pixel body — five-band silvery gradient with subtle limb darkening
    for (let dy = -moonR; dy <= moonR; dy += mps) {
      for (let dx = -moonR; dx <= moonR; dx += mps) {
        const d2 = dx * dx + dy * dy
        if (d2 <= moonR * moonR) {
          const d = Math.sqrt(d2) / moonR
          if (d < 0.14) ctx.fillStyle = '#FFFFFF'
          else if (d < 0.34) ctx.fillStyle = '#EDF1FA'
          else if (d < 0.56) ctx.fillStyle = '#D4DCEE'
          else if (d < 0.76) ctx.fillStyle = '#BFCAE0'
          else ctx.fillStyle = '#AAB6D0'
          ctx.fillRect(Math.floor(moonX + dx), Math.floor(moonY + dy), mps, mps)
        }
      }
    }

    // Craters — floor (darker) + upper-left rim highlight (brighter)
    const craters = [
      { cx: moonR * 0.28, cy: -moonR * 0.22, r: moonR * 0.2, floor: '#B0BAD4', rim: '#CDD5E8' },
      { cx: -moonR * 0.18, cy: moonR * 0.32, r: moonR * 0.15, floor: '#ACBAD0', rim: '#C8D0E4' },
      { cx: moonR * 0.08, cy: moonR * 0.52, r: moonR * 0.1, floor: '#B4BED6', rim: '#D0D8E8' },
      { cx: -moonR * 0.35, cy: -moonR * 0.08, r: moonR * 0.09, floor: '#B0BAD2', rim: '#CBD3E5' },
      { cx: moonR * 0.45, cy: moonR * 0.2, r: moonR * 0.07, floor: '#B8C4D8', rim: '#D4DCE8' },
      { cx: -moonR * 0.08, cy: -moonR * 0.5, r: moonR * 0.08, floor: '#B4C0D6', rim: '#D0D8E8' },
    ]
    for (const cr of craters) {
      for (let dy = -cr.r; dy <= cr.r; dy += mps) {
        for (let dx = -cr.r; dx <= cr.r; dx += mps) {
          if (dx * dx + dy * dy <= cr.r * cr.r) {
            ctx.fillStyle = cr.floor
            ctx.fillRect(Math.floor(moonX + cr.cx + dx), Math.floor(moonY + cr.cy + dy), mps, mps)
          }
        }
      }
      // Upper-left rim highlight
      const rimR = cr.r + mps * 1.2
      for (let dy = -rimR; dy <= mps; dy += mps) {
        for (let dx = -rimR; dx <= mps; dx += mps) {
          const d2 = dx * dx + dy * dy
          if (d2 <= rimR * rimR && d2 > cr.r * cr.r) {
            ctx.fillStyle = cr.rim
            ctx.fillRect(Math.floor(moonX + cr.cx + dx), Math.floor(moonY + cr.cy + dy), mps, mps)
          }
        }
      }
    }

    // Phase shadow — proper elliptical terminator (no more straight rectangle cuts)
    // Uses two cubic bezier curves approximating a half-ellipse for the terminator curve.
    ctx.save()
    ctx.beginPath()
    ctx.arc(moonX, moonY, moonR + 1, 0, Math.PI * 2)
    ctx.clip()
    ctx.fillStyle = 'rgba(5, 8, 14, 0.97)'

    const kBez = 0.5523 // bezier quarter-ellipse constant (4/3 * tan(π/8))

    if (moonPhase < 0.02 || moonPhase > 0.98) {
      // New moon: fully dark
      ctx.beginPath()
      ctx.arc(moonX, moonY, moonR + 2, 0, Math.PI * 2)
      ctx.fill()
    } else if (moonPhase < 0.48 || moonPhase > 0.52) {
      // General case: crescent or gibbous (skip very near full moon to avoid artefacts)
      const isWaxing = moonPhase <= 0.5
      // termOff: x-offset of the terminator ellipse's extremal point from moon center
      // positive → bows toward lit side (crescent shadow); negative → bows into dark side (gibbous shadow)
      const termOff = isWaxing ? darkOx + moonR : darkOx - moonR
      const ax = Math.abs(termOff)
      const dir = Math.sign(termOff) || 1

      ctx.beginPath()
      ctx.moveTo(moonX, moonY - moonR)
      if (isWaxing) {
        // CCW arc: top → left side → bottom (covers dark left side)
        ctx.arc(moonX, moonY, moonR + 1, -Math.PI / 2, Math.PI / 2, true)
      } else {
        // CW arc: top → right side → bottom (covers dark right side)
        ctx.arc(moonX, moonY, moonR + 1, -Math.PI / 2, Math.PI / 2, false)
      }
      // Half-ellipse terminator: two quarter-ellipse beziers from bottom back to top
      ctx.bezierCurveTo(
        moonX + dir * ax * kBez,
        moonY + moonR,
        moonX + dir * ax,
        moonY + moonR * kBez,
        moonX + dir * ax,
        moonY
      )
      ctx.bezierCurveTo(
        moonX + dir * ax,
        moonY - moonR * kBez,
        moonX + dir * ax * kBez,
        moonY - moonR,
        moonX,
        moonY - moonR
      )
      ctx.closePath()
      ctx.fill()
    }
    ctx.restore()

    stars.forEach((s) => {
      s.update()
      s.draw()
    })
    nextMeteorFrame--
    if (nextMeteorFrame <= 0) {
      shootingStars.push(new ShootingStar())
      nextMeteorFrame = Math.random() * 900 + 300
    }
    for (let i = shootingStars.length - 1; i >= 0; i--) {
      const active = shootingStars[i].update()
      shootingStars[i].draw()
      if (!active) shootingStars.splice(i, 1)
    }

    // Ambient light around mouse/flashlight in night mode
    if (mouse.x !== 0 || mouse.y !== 0) {
      ctx.save()
      const spotlightRadius = 260
      const gradient = ctx.createRadialGradient(
        mouse.x,
        mouse.y,
        0,
        mouse.x,
        mouse.y,
        spotlightRadius
      )
      gradient.addColorStop(0, 'rgba(173, 255, 47, 0.18)')
      gradient.addColorStop(0.3, 'rgba(173, 255, 47, 0.08)')
      gradient.addColorStop(0.6, 'rgba(173, 255, 47, 0.02)')
      gradient.addColorStop(1, 'rgba(0, 0, 0, 0)')
      ctx.fillStyle = gradient
      ctx.fillRect(0, 0, width, height)
      ctx.restore()
    }
  }

  // ── Physics leaves — spawn from canopy edges, visible in both day and night ─
  if (canopies.length > 0 && physicsLeaves.length < 22 && Math.random() < 0.009) {
    const canopy = canopies[Math.floor(Math.random() * canopies.length)]
    const sway = Math.sin(canopy.swayPhase) * canopy.swayAmp
    const baseX = canopy.side === 'left' ? -20 + sway : width + 20 + sway
    // Cap inset at 55px so leaves always appear right at the visible tree edge
    const inset = Math.min(canopy.scale * (4 + Math.random() * 10), 55)
    const spawnX = canopy.side === 'left' ? baseX + inset : baseX - inset
    const spawnY = canopy.y + (Math.random() - 0.5) * canopy.scale * 12
    physicsLeaves.push(new PhysicsLeaf(spawnX, spawnY))
  }
  for (let i = physicsLeaves.length - 1; i >= 0; i--) {
    physicsLeaves[i].update()
    physicsLeaves[i].draw()
    if (physicsLeaves[i].dead) physicsLeaves.splice(i, 1)
  }

  // 2. Lazy-init extra canvases in case Teleport mounted after onMounted
  if (!ctxMid && canvasMidRef.value) {
    ctxMid = canvasMidRef.value.getContext('2d')
    if (ctxMid) {
      ctxMid.imageSmoothingEnabled = false
      canvasMidRef.value.width = width
      canvasMidRef.value.height = height
    }
  }
  if (!ctxFg && canvasFgRef.value) {
    ctxFg = canvasFgRef.value.getContext('2d')
    if (ctxFg) {
      ctxFg.imageSmoothingEnabled = false
      canvasFgRef.value.width = width
      canvasFgRef.value.height = height
    }
  }
  if (!ctxDeepBg && props.deepBgCanvas) {
    ctxDeepBg = props.deepBgCanvas.getContext('2d')
    if (ctxDeepBg) {
      ctxDeepBg.imageSmoothingEnabled = false
      props.deepBgCanvas.width = width
      props.deepBgCanvas.height = height
    }
  }

  insects.forEach((insect) => insect.update())

  // ── Perch selection: still mouse → foreground butterflies land on cursor ──
  if (isDay.value && mouse.x > 0) {
    const now = performance.now()
    const stillTime = now - lastMouseMoveTime
    const maxPerched = 2
    const required = perchedInsects.length === 0 ? STILL_DURATION : STILL_DURATION * 2.2

    if (stillTime > required && perchedInsects.length < maxPerched) {
      // Collect eligible butterflies
      const eligible = []
      for (const insect of insects) {
        if (!insect.isForeground || insect.landPhase !== 0) continue
        if (perchedInsects.includes(insect)) continue
        const d = Math.hypot(insect.x - mouse.x, insect.y - mouse.y)
        if (d < 420) eligible.push({ insect, dist: d })
      }

      if (eligible.length > 0) {
        // Pick: 65% chance closest, 35% random among top 3 (variety)
        eligible.sort((a, b) => a.dist - b.dist)
        let chosen
        if (Math.random() < 0.65 || eligible.length === 1) {
          chosen = eligible[0].insect
        } else {
          const pool = eligible.slice(0, Math.min(3, eligible.length))
          chosen = pool[Math.floor(Math.random() * pool.length)].insect
        }

        // Slight offset so 2 butterflies don't overlap exactly
        const offX = perchedInsects.length > 0 ? (Math.random() - 0.5) * 22 : 0
        const offY = perchedInsects.length > 0 ? (Math.random() - 0.5) * 16 : 0
        chosen.startLanding(mouse.x + offX, mouse.y + 10 + offY)
        perchedInsects.push(chosen)
      }
    }
  }

  // Layer 1 — deep bg (behind text, absolute canvas inside section z-0)
  if (ctxDeepBg) {
    ctxDeepBg.clearRect(0, 0, width, height)
    insects.forEach((insect) => {
      if (insect.isDeepBg) insect.draw(ctxDeepBg)
    })
  }

  // Layer 2 — mid (above text, below tooltip — teleport z-25)
  if (ctxMid) {
    ctxMid.clearRect(0, 0, width, height)
    insects.forEach((insect) => {
      if (!insect.isForeground && !insect.isDeepBg) insect.draw(ctxMid)
    })
  }

  // Layer 3 — fg (above tooltip, below cursor — teleport z-65)
  if (ctxFg) {
    ctxFg.clearRect(0, 0, width, height)
    insects.forEach((insect) => {
      if (insect.isForeground) insect.draw(ctxFg)
    })
  }

  // ── Foreground clouds (day only) — canvas sits above text (z-50) ──────────
  // Lazy-init in case Teleport mounts after onMounted
  if (!ctxFgClouds && canvasFgCloudsRef.value) {
    ctxFgClouds = canvasFgCloudsRef.value.getContext('2d')
    if (ctxFgClouds) {
      ctxFgClouds.imageSmoothingEnabled = false
      canvasFgCloudsRef.value.width = width
      canvasFgCloudsRef.value.height = height
    }
  }
  if (ctxFgClouds) {
    ctxFgClouds.clearRect(0, 0, width, height)
    if (isDay.value) {
      // Temporarily redirect ctx so PixelCloud._puff / .draw() paint on ctxFgClouds
      const savedCtx = ctx
      ctx = ctxFgClouds
      fgClouds.forEach((c) => {
        c.update()
        c.draw()
      })
      ctx = savedCtx
    }
  }

  if (isAnimating) {
    animationFrameId = requestAnimationFrame(animate)
  }
}

const checkTheme = () => {
  const isDark = document.documentElement.classList.contains('dark')
  const newIsDay = !isDark

  if (isDay.value !== newIsDay) {
    isDay.value = newIsDay

    perchedInsects = []
    physicsLeaves = []

    if (!isDay.value) {
      initStars()
    } else {
      initClouds()
      initFgClouds()
    }
    initCanopies()
    initInsects()
  }
}

const resize = () => {
  if (canvasRef.value) {
    width = window.innerWidth
    height = window.innerHeight
    canvasRef.value.width = width
    canvasRef.value.height = height
    ctx.imageSmoothingEnabled = false

    if (canvasMidRef.value) {
      canvasMidRef.value.width = width
      canvasMidRef.value.height = height
      if (ctxMid) ctxMid.imageSmoothingEnabled = false
    }

    if (canvasFgRef.value) {
      canvasFgRef.value.width = width
      canvasFgRef.value.height = height
      if (ctxFg) ctxFg.imageSmoothingEnabled = false
    }

    if (canvasFgCloudsRef.value) {
      canvasFgCloudsRef.value.width = width
      canvasFgCloudsRef.value.height = height
      if (ctxFgClouds) ctxFgClouds.imageSmoothingEnabled = false
    }

    if (props.deepBgCanvas) {
      props.deepBgCanvas.width = width
      props.deepBgCanvas.height = height
      if (ctxDeepBg) ctxDeepBg.imageSmoothingEnabled = false
    }

    physicsLeaves = []
    if (!isDay.value) initStars()
    else {
      initClouds()
      initFgClouds()
    }
    initCanopies()

    initInsects()
  }
}

const handleMouseMove = (e) => {
  const now = performance.now()
  const dt = now - prevMouseTime
  const dx = e.clientX - mouse.x
  const dy = e.clientY - mouse.y

  mouse.x = e.clientX
  mouse.y = e.clientY

  // Any movement resets the stillness clock (prevents new landings while moving)
  if (dx !== 0 || dy !== 0) lastMouseMoveTime = now
  prevMouseTime = now

  // Only fast movement triggers takeoff; slow/gentle movement is ignored
  if (perchedInsects.length > 0 && dt > 0 && dt < 80) {
    const speed = Math.sqrt(dx * dx + dy * dy) / dt
    if (speed > TAKEOFF_VELOCITY) {
      for (const insect of perchedInsects) insect.takeOff()
      perchedInsects = []
    }
  }
}

// --- EXTERNAL CONTROL ---
const setInteractionPoint = (x, y) => {
  const now = performance.now()
  const dt = now - prevMouseTime
  const dx = x - mouse.x
  const dy = y - mouse.y
  mouse.x = x
  mouse.y = y

  if (dx !== 0 || dy !== 0) lastMouseMoveTime = now
  prevMouseTime = now

  if (perchedInsects.length > 0 && dt > 0 && dt < 80) {
    const speed = Math.sqrt(dx * dx + dy * dy) / dt
    if (speed > TAKEOFF_VELOCITY) {
      for (const insect of perchedInsects) insect.takeOff()
      perchedInsects = []
    }
  }
}

defineExpose({
  setInteractionPoint,
})

onMounted(() => {
  const canvas = canvasRef.value
  ctx = canvas.getContext('2d')

  if (canvasMidRef.value) {
    ctxMid = canvasMidRef.value.getContext('2d')
    ctxMid.imageSmoothingEnabled = false
  }

  if (canvasFgRef.value) {
    ctxFg = canvasFgRef.value.getContext('2d')
    ctxFg.imageSmoothingEnabled = false
  }

  if (canvasFgCloudsRef.value) {
    ctxFgClouds = canvasFgCloudsRef.value.getContext('2d')
    ctxFgClouds.imageSmoothingEnabled = false
  }

  resize()
  checkTheme()

  if (!isDay.value && stars.length === 0) initStars()
  if (isDay.value && clouds.length === 0) {
    initClouds()
    initFgClouds()
  }
  if (canopies.length === 0) initCanopies()
  if (insects.length === 0) initInsects()

  window.addEventListener('resize', resize)
  window.addEventListener('mousemove', handleMouseMove)

  observer = new MutationObserver(checkTheme)
  observer.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['class'],
  })

  if (props.visible) {
    startAnimating()
  }
})

onUnmounted(() => {
  stopAnimating()
  window.removeEventListener('resize', resize)
  window.removeEventListener('mousemove', handleMouseMove)
  if (observer) observer.disconnect()
})
</script>

<template>
  <div class="bioma-container">
    <div class="vignette" :class="{ 'vignette--day': isDay, 'vignette--night': !isDay }"></div>
    <canvas ref="canvasRef" class="bioma-canvas"></canvas>
  </div>
  <!-- Mid insects: above text, below tooltip (z-25) -->
  <Teleport to="body">
    <canvas
      ref="canvasMidRef"
      :style="{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        pointerEvents: 'none',
        zIndex: 25,
        imageRendering: 'pixelated',
        opacity: props.visible ? 1 : 0,
        transition: 'opacity 0.5s ease',
      }"
    ></canvas>
  </Teleport>
  <!-- Foreground clouds: above text (z-50), visible only in day mode -->
  <Teleport to="body">
    <canvas
      ref="canvasFgCloudsRef"
      :style="{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        pointerEvents: 'none',
        zIndex: 50,
        imageRendering: 'pixelated',
        opacity: props.visible && isDay ? 1 : 0,
        transition: 'opacity 0.5s ease',
      }"
    ></canvas>
  </Teleport>
  <!-- Foreground insects: above clouds (z-65) -->
  <Teleport to="body">
    <canvas
      ref="canvasFgRef"
      :style="{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        pointerEvents: 'none',
        zIndex: 65,
        imageRendering: 'pixelated',
        opacity: props.visible ? 1 : 0,
        transition: 'opacity 0.5s ease',
      }"
    ></canvas>
  </Teleport>
</template>

<style scoped>
.bioma-container {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  pointer-events: none;
  z-index: 50;
}

.bioma-canvas {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 2;
  image-rendering: pixelated;
}

.vignette {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
  transition: background 1.5s ease-in-out;
}

.vignette--day {
  background:
    /* Warm glow near the sun */
    radial-gradient(ellipse 140% 50% at 82% 14%, rgba(255, 240, 170, 0.28) 0%, transparent 60%),
    /* Sky gradient */
      linear-gradient(
        to bottom,
        rgba(100, 180, 230, 0.55) 0%,
        rgba(150, 205, 240, 0.38) 30%,
        rgba(200, 225, 250, 0.18) 60%,
        rgba(235, 245, 255, 0.06) 100%
      );
}

.vignette--night {
  background: radial-gradient(
    circle,
    rgba(0, 0, 0, 0) 30%,
    rgba(0, 0, 10, 0.4) 70%,
    rgba(0, 0, 15, 0.8) 100%
  );
}
</style>
